import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { CompetitionService } from '../../service/CompetitionService';
import { translatedMessage } from '../../service/LanguageService';
import { useNavigate } from 'react-router-dom';
import GeneralUtils from '../../utilities/GeneralUtils';
import EnumService from '../../service/EnumService';
import DeleteModalComponent from '../../components/DeleteModalComponent';
import DataTableUtils from '../../utilities/DataTableUtils';
import CompetitionDataTableUtils from '../../utilities/CompetitionDataTableUtils';
import useAuth from '../../hooks/useAuth';
import ExportButtonComponent from '../../components/ExportButtonComponent';
import DataTableExportUtils from '../../utilities/DataTableExportUtils';
import { InstitutionService } from '../../service/InstitutionService';
import CompetitionJobEditDialog from './CompetitionJobEditDialog';
import CompetitionUtils from '../../utilities/CompetitionUtils';

const CompetitionJobList = () => {
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [competitionStatuses, setCompetitionStatuses] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [competitionCategories, setCompetitionCategories] = useState([]);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: DataTableUtils.defalRowsPerPage(),
        page: 1,
        sortField: 'id',
        sortOrder: 1,
        filters: {
            name: { value: null, matchMode: FilterMatchMode.CONTAINS },
            type: { value: null, matchMode: FilterMatchMode.CONTAINS },
            positionCategory: { value: null, matchMode: FilterMatchMode.IN },
            institution: { value: null, matchMode: FilterMatchMode.IN },
            status: { value: null, matchMode: FilterMatchMode.IN },
            position: { value: null, matchMode: FilterMatchMode.CONTAINS },
            departmentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        }
    });
    const [lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession] = useState(false);
    const propToColumnMap = {
        name: 'name',
        status: 'status',
        type: 'type',
        positionCategory: 'position_category',
        startDate: 'start_date',
        submitEndDate: 'submit_end_date',
        position: 'position',
        departmentName: 'department_name',
    };
    const [totalRecords, setTotalRecords] = useState(0);

    const [firstLoading, setFirstLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);

    const toast = useRef(null);
    const dt = useRef(null);

    const { auth } = useAuth();
    const navigate = useNavigate();

    const competitionService = useMemo(() => new CompetitionService(), []);
    const institutionService = useMemo(() => new InstitutionService(), []);

    useEffect(() => {
        setFirstLoading(true);

        const getCompetitionStatuses = async () => {
            setCompetitionStatuses(await EnumService.getEnumByName('CompetitionStatus'));
        };

        const getCompetitionCategories = async () => {
            setCompetitionCategories(await EnumService.getEnumByName('PositionCategory', 'job'));
        };

        const getInstitutions = async () => {
            await institutionService.getInstitutionsForUserByCriteria({ name: null })
                .then((_institutions) => {
                    // console.log("institutions: ", _institutions);
                    setInstitutions(_institutions);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getCompetitionStatuses();
        getCompetitionCategories();
        getInstitutions()
        setFirstLoading(false);
    }, [competitionService, institutionService]);

    useEffect(() => {
        loadCompetitions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams]);

    const setCriteria = () => {
        GeneralUtils.processSessionFilterValues('CompetitionJobAdministrationList', lazyParams, lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession);

        let criteria = {};
        criteria.sortOrder = lazyParams.sortOrder < 0 ? 'DESC' : 'ASC';
        criteria.sortField = propToColumnMap[lazyParams.sortField] || 'id';
        criteria.name = lazyParams.filters.name.value;
        criteria.statuses = lazyParams.filters.status.value;
        criteria.positionCategories = lazyParams.filters.positionCategory.value;
        criteria.institutions = lazyParams.filters.institution.value
        criteria.position = lazyParams.filters.position.value;
        criteria.departmentName = lazyParams.filters.departmentName.value;

        return criteria;
    };

    const loadCompetitions = async () => {
        setIsLoading(true);

        let criteria = setCriteria();
        criteria.startRow = lazyParams.first;
        criteria.pageSize = lazyParams.rows;

        await competitionService
            .getCompetitionsJobByCriteria(criteria)
            .then((data) => {
                // console.log(data.competitions);
                setCompetitions(data.competitions);
                setTotalRecords(data.totalCount);
                setIsLoading(false);
            })
            .catch((error) => {
                //setError(true)
                toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            });
    };

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    const updateList = async (savedCompetition, isNew, success, error) => {
        if (success) {
            loadCompetitions()
            toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
        }
    };

    const showCompetitionViewPage = (competition) => {
        navigate(`/competition-job/${competition.id}`);
    };

    const showNewDialog = () => {
        setSelectedCompetition(null);
        setEditDialogVisible(true);
    };

    const showEditDialog = (competition) => {
        setSelectedCompetition(competition);
        setEditDialogVisible(true);
    };

    const showDeleteDialog = (competition) => {
        setSelectedCompetition(competition);
        setDeleteDialogVisible(true);
    };

    const deleteCompetition = () => {
        competitionService
            .deleteCompetition(selectedCompetition.id)
            .then(() => {
                loadCompetitions();
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedCompetition(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {(auth.user?.institutionResponsible || hasPermission('COMPETITION_JOB_EDIT')) &&
                        <>
                            <Button label={translatedMessage('competition.new.job')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog} />
                        </>
                    }
                </div>
            </React.Fragment>
        );
    };

    const handleExport = () => {
        let criteria = setCriteria();
        return new Promise((resolve, reject) => {
            competitionService.getCompetitionsJobByCriteria(criteria)
                .then((data) => {
                    let exportData = data.competitions.map((item) => ({ ...item }));
                    // console.log(exportData);
                    exportData.map((item) => {
                        let exportItem = item;
                        exportItem.status = CompetitionUtils.getCompetitionStatus(item)
                        // exportItem.type = translatedMessage('CompetitionType.' + item.type)
                        exportItem.positionCategory = item.positionCategory ? translatedMessage('job.PositionCategory.' + item.positionCategory) : ""
                        exportItem.institution = item.institution.name
                        exportItem.hasResponsibility = GeneralUtils.yesNoConverter(item.hasResponsibility)
                        exportItem.anfpRequest = GeneralUtils.yesNoConverter(item.anfpRequest)

                        delete exportItem.form
                        delete exportItem.testSessionsDistributionFinished
                        delete exportItem.eligibilityFinished
                        delete exportItem.description
                        delete exportItem.resultType
                        delete exportItem.allowTestSessions
                        delete exportItem.bibliography
                        delete exportItem.lastModifiedOn
                        delete exportItem.testSessionsChooseEnabled
                        delete exportItem.type
                        delete exportItem.workSeniorityRequired
                        delete exportItem.workSeniority
                        delete exportItem.trainingRequired
                        delete exportItem.training
                        delete exportItem.writtenTestLocation
                        delete exportItem.managerName
                        delete exportItem.managerPosition
                        delete exportItem.canEditWrittenTestDate
                        delete exportItem.changeReason
                        delete exportItem.contactEmail
                        delete exportItem.contactName
                        delete exportItem.contactPhone
                        delete exportItem.contactPosition
                        delete exportItem.jobApproval
                        delete exportItem.jobApprovalNormativeAct
                        delete exportItem.jobApprovalRequired
                        delete exportItem.legalBasis
                        delete exportItem.otherCondition
                        delete exportItem.positionGrade
                        delete exportItem.positionType
                        delete exportItem.overallTestsPassingScore
                        delete exportItem.folder
                        delete exportItem.supplementaryTest1Name
                        delete exportItem.supplementaryTest1Date
                        delete exportItem.supplementaryTest2Name
                        delete exportItem.supplementaryTest2Date
                        delete exportItem.supplementaryTest3Name
                        delete exportItem.supplementaryTest3Date
                        delete exportItem.canViewSubjects
                        delete exportItem.responsibleEmail
                        delete exportItem.responsibleName
                        delete exportItem.responsiblePhone
                        delete exportItem.responsiblePosition
                        delete exportItem.jobDuty
                        delete exportItem.workingHours
                        delete exportItem.institutionRegistrationDate
                        delete exportItem.institutionRegistrationNumber


                        return exportItem
                    });

                    resolve(exportData)
                })
                .catch((error) => reject(error));
        });
    };

    const rightToolbarTemplate = () => {
        return <ExportButtonComponent
            getExportData={handleExport}
            header={DataTableExportUtils.competitionJobListTableHeader()}
            sortOrderHeader={DataTableExportUtils.competitionJobListSortOrderHeader()}
            fileName={GeneralUtils.replaceSpaces(translatedMessage('competition.competitions'))}
        />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {(hasPermission('COMPETITION_JOB_VIEW') || auth.user?.institutionResponsible) && (
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showCompetitionViewPage(rowData)} tooltip={translatedMessage('generic.view')} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                )}
                {(hasPermission('COMPETITION_JOB_EDIT') || auth.user?.institutionResponsible) && (
                    <Button icon="pi pi-pencil" 
                        className="p-button-rounded p-button-info m-1" 
                        onClick={() => showEditDialog(rowData)} 
                        tooltip={translatedMessage('generic.edit')} 
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }} 
                        disabled={!CompetitionUtils.canEdit(rowData)}
                    />
                )}
                {(hasPermission('COMPETITION_JOB_EDIT') || auth.user?.institutionResponsible) && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger m-1"
                        onClick={() => showDeleteDialog(rowData)}
                        disabled={!CompetitionUtils.canEdit(rowData)}
                        tooltip={translatedMessage('generic.delete')}
                        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                    />
                )}
            </div>
        );
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.first = event.first;
        _lazyParams.page = event.page;
        _lazyParams.rows = event.rows;
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.sortField = event.sortField;
        _lazyParams.sortOrder = event.sortOrder;
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };

    return (
        <>
            <Toast ref={toast} />
            {firstLoading && (
                <div className="w-full flex align-items-center">
                    <ProgressSpinner />
                </div>
            )}
            {!firstLoading && (
                <>
                    <div className="grid h-full">
                        <div className="col-12">
                            <div className="card h-full">
                                <h3 className="mb-0">{translatedMessage('competition.job.competitions')}</h3>

                                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                                <DataTable
                                    ref={dt}
                                    value={competitions}
                                    dataKey="id"
                                    paginator
                                    lazy
                                    totalRecords={totalRecords}
                                    first={lazyParams.first}
                                    rows={lazyParams.rows}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onPage={onPage}
                                    onSort={onSort}
                                    onFilter={onFilter}
                                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                    className="datatable-responsive pcn-datatable"
                                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                    currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                    responsiveLayout="scroll"
                                    filterDisplay="row"
                                    filters={lazyParams.filters}
                                    loading={isLoading}
                                >
                                    <Column
                                        field="id"
                                        header={translatedMessage('generic.id')}
                                        sortable
                                        headerStyle={{ width: '5%', minWidth: '3rem' }}
                                    />
                                    <Column
                                        field="name"
                                        header={translatedMessage('generic.nameRo')}
                                        sortable
                                        headerStyle={{ width: '15%', minWidth: '8rem' }}
                                        filter
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="positionCategory"
                                        header={translatedMessage('competition.positionCategory')}
                                        sortable
                                        body={(e) => CompetitionDataTableUtils.jobCategoryBodyTemplate(e.positionCategory)}
                                        headerStyle={{ width: '12%', minWidth: '8rem' }}
                                        filter
                                        filterElement={(e) => CompetitionDataTableUtils.categoryFilterTemplate(e, competitionCategories, true)}
                                        filterHeaderClassName="pcn-filter-column-200"
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="institution"
                                        header={translatedMessage('competition.institution')}
                                        sortable
                                        headerStyle={{ width: '15%', minWidth: '8rem' }}
                                        body={(e) => e.institution.name}
                                        filter
                                        filterField="institution"
                                        filterElement={(e) => CompetitionDataTableUtils.institutionFilterTemplate(e, institutions)}
                                        filterHeaderClassName="pcn-filter-column-250"
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="status"
                                        header={translatedMessage('generic.status')}
                                        sortable
                                        body={(e) => CompetitionDataTableUtils.statusBodyTemplate(e)}
                                        headerStyle={{ width: '10%', minWidth: '8rem' }}
                                        filter
                                        filterElement={(e) => CompetitionDataTableUtils.statusFilterTemplate(e, competitionStatuses)}
                                        filterHeaderClassName="pcn-filter-column-200"
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="departmentName"
                                        header={translatedMessage('job.departmentName')}
                                        sortable
                                        body={(e) => GeneralUtils.dashIfNullForObject(e.departmentName)}
                                        headerStyle={{ width: '8%', minWidth: '8rem' }}
                                        filter
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="position"
                                        header={translatedMessage('job.position')}
                                        sortable
                                        body={(e) => GeneralUtils.dashIfNullForObject(e.position)}
                                        headerStyle={{ width: '8%', minWidth: '8rem' }}                                        
                                        filter
                                        showFilterMenu={false}
                                    />
                                    <Column
                                        field="startDate"
                                        header={translatedMessage('competition.job.startDate')}
                                        sortable
                                        body={(e) => DataTableUtils.dateTemplate(e.startDate)}
                                        headerStyle={{ width: '6%', minWidth: '6rem' }}
                                    />
                                    <Column
                                        field="writtenTestDate"
                                        header={translatedMessage('competition.writtenTestDate')}
                                        sortable
                                        body={(e) => DataTableUtils.dateTemplate(e.writtenTestDate)}
                                        headerStyle={{ width: '6%', minWidth: '6rem' }}
                                    />
                                    <Column body={actionBodyTemplate} />
                                </DataTable>

                                {editDialogVisible &&
                                    <CompetitionJobEditDialog
                                        value={selectedCompetition}
                                        visible={editDialogVisible}
                                        afterSave={updateList}
                                        visibleSetter={setEditDialogVisible}
                                        statuses={competitionStatuses}
                                        institutions={institutions}
                                    />
                                }

                                {deleteDialogVisible &&
                                    <DeleteModalComponent
                                        visible={deleteDialogVisible}
                                        item={selectedCompetition ? selectedCompetition.name : ''}
                                        closeDialog={() => {
                                            setDeleteDialogVisible(false);
                                        }}
                                        deleteRecord={() => deleteCompetition()}
                                        info="competition.delete.info"
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default CompetitionJobList;
